import React, { createContext, useContext, useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { authAPI } from '../services/api';
import toast from 'react-hot-toast';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const queryClient = useQueryClient();

  // Set token in localStorage and API headers
  useEffect(() => {
    if (token) {
      localStorage.setItem('token', token);
      authAPI.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      localStorage.removeItem('token');
      delete authAPI.defaults.headers.common['Authorization'];
    }
  }, [token]);

  // Fetch current user
  const { isLoading, refetch: refetchUser } = useQuery(
    'currentUser',
    () => authAPI.get('/auth/me'),
    {
      enabled: !!token,
      retry: 2,
      retryDelay: 2000,
      staleTime: 5 * 60 * 1000, // 5 minutes
      refetchOnWindowFocus: false,
      onSuccess: (response) => {
        console.log('User data fetched successfully:', response.data);
        setUser(response.data.data);
      },
      onError: (error) => {
        console.error('Auth error:', error);
        if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
          console.log('Request timed out, but keeping token for retry');
          // Don't clear token on timeout, just log the error
          return;
        }
        const status = error.response?.status;
        // Keep land reporter signed in on transient errors; only logout on auth failures.
        if (user?.role === 'land_reporter' && status !== 401 && status !== 419) {
          console.log('Non-auth error for land reporter, keeping token');
          return;
        }
        setToken(null);
        setUser(null);
        localStorage.removeItem('token');
      }
    }
  );

  // Send verification code mutation (locale = language for the sign-in email)
  const sendVerificationCodeMutation = useMutation(
    (data) => authAPI.post('/auth/send-verification', data),
    {
      onSuccess: () => {
        // Toast wird in der LoginPage angezeigt
      },
      onError: (error) => {
        const message = error.response?.data?.message || 'Failed to send verification code';
        toast.error(message);
        throw new Error(message);
      }
    }
  );

  // Verify code mutation
  const verifyCodeMutation = useMutation(
    ({ email, verificationCode }) => {
      const referralCode = localStorage.getItem('referral_code');
      return authAPI.post('/auth/verify', {
        email,
        verification_code: verificationCode,
        referral_code: referralCode || undefined,
      });
    },
    {
      onSuccess: (response) => {
        const { user: userData, token: userToken } = response.data.data;
        console.log('Login successful, setting user and token:', { userData, userToken });
        if (userToken) {
          localStorage.setItem('token', userToken);
          authAPI.defaults.headers.common['Authorization'] = `Bearer ${userToken}`;
        }
        setUser(userData);
        setToken(userToken);
        // Toast wird in der LoginPage angezeigt
      },
      onError: (error) => {
        const message = error.response?.data?.message || 'Invalid verification code';
        toast.error(message);
        throw new Error(message);
      }
    }
  );

  // Logout mutation
  const logoutMutation = useMutation(
    () => authAPI.post('/auth/logout'),
    {
      onSuccess: () => {
        setUser(null);
        setToken(null);
        queryClient.clear();
        toast.success('Logged out successfully');
      },
      onError: () => {
        setUser(null);
        setToken(null);
        queryClient.clear();
      }
    }
  );

  // Update language mutation
  const updateLanguageMutation = useMutation(
    (language) => authAPI.put('/auth/language', { language }),
    {
      onMutate: async (language) => {
        // Optimistically update user state so the UI language switches immediately
        // (LanguageContext prefers user.preferred_language when available).
        const previousUser = user;

        setUser((prev) => (prev ? { ...prev, preferred_language: language } : prev));

        return { previousUser };
      },
      onSuccess: (_response, language) => {
        toast.success('Language updated successfully');

        // Best-effort: refresh user in background so backend & frontend stay in sync
        queryClient.invalidateQueries('currentUser');
      },
      onError: (error, _language, context) => {
        // Revert optimistic update if request failed
        if (context?.previousUser !== undefined) {
          setUser(context.previousUser);
        }
        toast.error(error.response?.data?.message || 'Failed to update language');
      }
    }
  );

  const value = {
    user,
    token,
    isLoading,
    isAuthenticated: !!user,
    isAdmin: user?.role === 'admin',
    isLandReporter: user?.role === 'land_reporter',
    isLandSeeker: user?.role === 'land_seeker',
    sendVerificationCode: async (data) => {
      return new Promise((resolve, reject) => {
        sendVerificationCodeMutation.mutate(data, {
          onSuccess: (response) => {
            resolve(response);
          },
          onError: (error) => {
            reject(error);
          }
        });
      });
    },
    verifyCode: async (data) => {
      return new Promise((resolve, reject) => {
        verifyCodeMutation.mutate(data, {
          onSuccess: (response) => {
            resolve(response);
          },
          onError: (error) => {
            reject(error);
          }
        });
      });
    },
    logout: logoutMutation.mutate,
    updateLanguage: updateLanguageMutation.mutate,
    isSendingCode: sendVerificationCodeMutation.isLoading,
    isVerifying: verifyCodeMutation.isLoading,
    isLoggingOut: logoutMutation.isLoading,
    refreshUser: refetchUser,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
